import { Stack } from '@fluentui/react';
import React from 'react';
import {
  CardAccountAvailableTags,
  CardAccountAvailableTagsVariables,
} from './__generated__/CardAccountAvailableTags';
import {
  GetCardAccountTags,
  GetCardAccountTagsVariables,
} from './__generated__/GetCardAccountTags';
import {
  EntityTagCardAccountCreate,
  EntityTagCardAccountCreateVariables,
} from './__generated__/EntityTagCardAccountCreate';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';
import { EntityTagInput } from 'common/types/globalTypes';
import { useStyles } from './index.styles';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';

import CARD_ACCOUNT_TAGS_LIST from './GetCardAccountTags.graphql';
import AVAILABLE_TAGS_LIST from './CardAccountAvailableTags.graphql';
import ADD_NEW_TAG from './EntityTagCardAccountCreate.graphql';
import DELETE_TAG from 'common/graphql/EntityTagDelete.graphql';

interface TagsSectionProps {
  cardAccountID?: string;
}

export const TagsSection: React.FC<TagsSectionProps> = ({ cardAccountID }) => {
  const styles = useStyles();

  // Extract available tags from the query data
  const extractAvailableTags = (data: CardAccountAvailableTags | undefined) => {
    return (
      data?.cardAccountAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? []
    );
  };

  // Extract entity tags from the query data
  const extractEntityTags = (data: GetCardAccountTags | undefined) => {
    return (
      data?.cardAccount?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown', // Handle null
      })) ?? []
    );
  };

  // Variables for fetching available tags
  const getAvailableTagsVars = (
    entityId: string
  ): CardAccountAvailableTagsVariables => ({
    id: entityId,
  });

  // Variables for fetching entity tags
  const getEntityTagsVars = (
    entityId: string
  ): GetCardAccountTagsVariables => ({
    id: entityId,
  });

  // Variables for creating new tags
  const createTagsVars = (
    tags: EntityTagInput[]
  ): EntityTagCardAccountCreateVariables => ({
    input: {
      entityTag: tags,
    },
  });

  // Variables for deleting tags
  const deleteTagsVars = (
    id: string,
    rowTimestamp: string
  ): EntityTagDeleteVariables => ({
    entityTagDeleteInput: {
      entityDelete: [{ id, rowTimestamp }],
    },
  });
  if (!cardAccountID) return null;
  return (
    <EntityTagsSection<
      CardAccountAvailableTags,
      CardAccountAvailableTagsVariables,
      GetCardAccountTags,
      GetCardAccountTagsVariables,
      EntityTagCardAccountCreate,
      EntityTagCardAccountCreateVariables,
      EntityTagDelete,
      EntityTagDeleteVariables
    >
      entityId={cardAccountID}
      getAvailableTagsGql={AVAILABLE_TAGS_LIST}
      getEntityTagsGql={CARD_ACCOUNT_TAGS_LIST}
      addTagGql={ADD_NEW_TAG}
      deleteTagGql={DELETE_TAG}
      extractAvailableTags={extractAvailableTags}
      extractEntityTags={extractEntityTags}
      getAvailableTagsVars={getAvailableTagsVars}
      getEntityTagsVars={getEntityTagsVars}
      createTagsVars={createTagsVars}
      deleteTagsVars={deleteTagsVars}
    />
  );
};
